import { useEffect, useMemo, useRef } from 'react';

import * as echarts from 'echarts';

import { BarChartProps } from './EChartsBarChartVerticalDots';
import { chartColors } from '../../configs/chart.config';

export const EChartsBarChartHorizontal = <T,>({
  data,
  keyField,
  valueField,
  yAxisLabel,
  formatKey,
  valueFormatOptions,
  width = '100%',
  height = '480px',
  className = '',
  chartMargin,
  hideTooltip = false,
}: BarChartProps<T>) => {
  const chartRef = useRef<HTMLDivElement | null>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);

  const chartOption = useMemo(() => {
    if (!data || data.length === 0) return null;

    const getKey = (item: T) =>
      typeof keyField === 'function' ? keyField(item) : (item as any)[keyField];

    const getValue = (item: T) => {
      if (!valueField) return 0;
      if (typeof valueField === 'function') return valueField(item);
      return Number((item as Record<keyof T, unknown>)[valueField as keyof T]);
    };

    const labels = data.map((item) => {
      const rawKey = getKey(item);
      return formatKey ? formatKey(String(rawKey)) : String(rawKey);
    });

    const values = data.map((item) => {
      const val = getValue(item);
      return val == null ? 0 : Number(val);
    });

    const isCompactView = parseInt(height, 10) <= 480;

    return {
      backgroundColor: '#1F1F1F',
      grid: {
        top: 35,
        left: 50,
        right: 20,
        bottom: 20,
        containLabel: true,
        ...(chartMargin || {}),
      },
      tooltip: hideTooltip
        ? undefined
        : {
            trigger: 'axis',
            axisPointer: {
              type: 'shadow',
            },
            backgroundColor: 'rgba(66,66,66,0.95)',
            borderWidth: 0,
            borderRadius: 4,

            padding: 12,
            textStyle: {
              color: '#fff',
              fontWeight: 'bold',
            },
            formatter: (params: any) => {
              if (!params.length) return '';
              const item = params[0];
              return `
                <div style="padding: 4px 8px; max-width: 300px;">
                  <p style="margin: 0; font-weight: bold; font-size: 12px; color: white;">
                    ${item.name}
                  </p>
                  <p style="margin: 0; font-size: 12px; color: ${item.color};">
                    ${item.seriesName}: ${new Intl.NumberFormat(undefined, valueFormatOptions).format(item.value)}
                  </p>
                </div>
              `;
            },
          },
      xAxis: {
        type: 'value',
        name: yAxisLabel,
        nameLocation: 'middle',
        nameTextStyle: {
          color: '#9E9E9E',
          fontSize: 12,
          padding: [15, 0, 0, 0],
        },
        axisLabel: {
          color: '#9E9E9E',
          ...(isCompactView && {
            formatter: (val: string) => {
              const num = Number(val);
              if (isNaN(num)) return val;
              return new Intl.NumberFormat('en-US', {
                notation: 'compact',
                compactDisplay: 'short',
                maximumFractionDigits: 2,
              }).format(num);
            },
          }),
        },
        splitLine: {
          lineStyle: { color: '#28303E' },
        },
      },
      yAxis: {
        type: 'category',
        data: labels,
        axisLine: { lineStyle: { color: '#28303E' } },
        axisLabel: {
          color: '#9E9E9E',
          margin: 12,
        },
      },

      series: [
        {
          name: yAxisLabel,
          type: 'bar',
          data: values,
          itemStyle: {
            color: (params: any) => {
              const index = params.dataIndex;
              return chartColors.colorPalette[index % chartColors.colorPalette.length];
            },
          },
          barMaxWidth: 32,
        },
      ],
    };
  }, [
    data,
    keyField,
    valueField,
    yAxisLabel,
    formatKey,
    valueFormatOptions,
    chartMargin,
    hideTooltip,
  ]);

  useEffect(() => {
    if (chartRef.current && chartOption) {
      if (!chartInstance.current) {
        chartInstance.current = echarts.init(chartRef.current);
      }
      chartInstance.current.setOption(chartOption);
    }
    return () => {
      chartInstance.current?.dispose();
      chartInstance.current = null;
    };
  }, [chartOption]);

  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center text-slate-500" style={{ width, height }}>
        No data available
      </div>
    );
  }

  return (
    <div className={`relative mx-auto overflow-hidden ${className}`}>
      <div
        ref={chartRef}
        style={{
          width,
          height,
          position: 'relative',
        }}
      />
    </div>
  );
};
